/* eslint-disable max-len */
// * This component takes optional props of initialCampus, showClearwater, and showOnline, defaulting to 45, true, and true, respectively.
// * When using this component to display only Online programs, invoke the component with showClearwater set to false.
// * When using this component to display only Clearwater programs, invoke the component with showOnline set to false and initialCampus set to '47'.
// * This component is also used on pages that accept URL parameters for campus. For those instances, do not include any props, as both Clearwater and Online programs should be shown; the useEffect will set state to Clearwater if Clearwater programs should be shown first.

/**
 * TODO
 *
 * Add dots to slider.
 *
 */

import React, { useEffect, useState } from 'react'

import { graphql, Link, useStaticQuery } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import htmr from 'htmr'
import _find from 'lodash/find'
import PropTypes from 'prop-types'
import { CarouselProvider, Slider, Slide } from 'pure-react-carousel'
import styled from 'styled-components'

import { below, colors } from '../../style/design-system'
import sp from '../../utils/filters/shortcodeParser'
import Card from '../elements/Card'
import 'pure-react-carousel/dist/react-carousel.es.css'

import EMTImage from '../../assets/images/emt/emt.jpg'

const ProgramSelector = ({ className, initialCampus, showClearwater, showOnline }) => {
  // Query all the programs
  const data = useStaticQuery(graphql`
    {
      programsOnlineDegree: allWpUmaprogram(
        filter: { metaProgramCampus: { eq: "45" }, metaProgramType: { eq: "degree" } }
      ) {
        edges {
          node {
            metaProgramCode
            metaProgramFullName
            metaProgramIsEnrolling
            metaProgramType
            uri
          }
        }
      }
      programsOnlineDiploma: allWpUmaprogram(
        filter: { metaProgramCampus: { eq: "45" }, metaProgramType: { eq: "diploma" } }
      ) {
        edges {
          node {
            metaProgramCode
            metaProgramFullName
            metaProgramIsEnrolling
            metaProgramType
            uri
          }
        }
      }
      programsClearwaterDiploma: allWpUmaprogram(
        filter: { metaProgramCampus: { eq: "47" }, metaProgramType: { eq: "diploma" } }
      ) {
        edges {
          node {
            metaProgramCode
            metaProgramFullName
            metaProgramIsEnrolling
            metaProgramType
            uri
          }
        }
      }
      programsClearwaterDegree: allWpUmaprogram(
        filter: { metaProgramCampus: { eq: "47" }, metaProgramType: { eq: "degree" } }
      ) {
        edges {
          node {
            metaProgramCode
            metaProgramFullName
            metaProgramIsEnrolling
            metaProgramType
            uri
          }
        }
      }
      markdownPrograms: allMarkdownRemark(filter: { fileAbsolutePath: { regex: "/programs/" } }) {
        edges {
          node {
            frontmatter {
              programCode
              desc
              hero {
                img {
                  childImageSharp {
                    gatsbyImageData(width: 300, quality: 90, placeholder: TRACED_SVG, layout: FIXED)
                  }
                }
              }
            }
          }
        }
      }
    }
  `)

  // set some static variables
  const gutterWidth = 30
  const slideWidth = 250
  const sliderUnit = 'px'

  // set some state variables
  const [campus, setCampus] = useState(initialCampus === '47' ? '47' : '45')
  const [onlineType, setOnlineType] = useState('degree')
  const [clearwaterType, setClearwaterType] = useState('diploma')

  // console.log('data', data)

  if (showOnline && showClearwater) {
    useEffect(() => {
      setCampus(initialCampus)
    }, [initialCampus])
  }

  const calculateSliderWidth = (programs) => {
    const sliderWidth = programs.length ? (gutterWidth + slideWidth) * programs.length : 0
    return `${sliderWidth}${sliderUnit}`
  }

  // render each individual program
  const renderProgram = (gatsbyImg, programUri, programDesc, programFullName, programIsEnrolling, programType) => {
    const programUriStrippedSuffix = programUri.replace('-degree', '').replace('-diploma', '')

    return (
      <Link to={programUriStrippedSuffix} className="program-card-wrapper d-flex">
        <Card cardShadow="none" className="program-card position-relative d-flex flex-column justify-content-between">
          <div className="card-body">
            <GatsbyImage image={gatsbyImg} alt={programFullName} className="mb-4" />
            <p className="mb-1">
              <strong>{programFullName}</strong>
              {!programIsEnrolling && <em>&nbsp;(not enrolling)</em>}
            </p>
            {programType === 'degree' && <p className="card__sub">Associate Degree</p>}
            {programType === 'diploma' && <p className="card__sub">Diploma</p>}
            <p>{htmr(sp(programDesc))}</p>
          </div>
          <div className="card-footer">
            <div className="btn btn-outline-black mb-4">Learn More &gt;</div>
          </div>
        </Card>
      </Link>
    )
  }

  // render the programs as a normal list for desktop
  // and as a slider on mobile
  const renderPrograms = (programs) => {
    const sliderWidth = calculateSliderWidth(programs)
    const sortedPrograms = programs
      // sorts alphabetically by program name
      .sort((a, b) => (a.node.metaProgramFullName > b.node.metaProgramFullName ? 1 : -1))
      // sorts to display programs that are NOT enrolling after programs that ARE enrolling
      .sort((a, b) => !!b.node.metaProgramIsEnrolling - !!a.node.metaProgramIsEnrolling)

    return (
      <>
        <div className="flex-wrap d-none d-md-flex row">
          {sortedPrograms.map(({ node }) => {
            const md = _find(
              data.markdownPrograms.edges,
              (o) => o.node.frontmatter.programCode === node.metaProgramCode,
            )

            return (
              <div key={node.metaProgramCode} className="col-8 col-md-6 col-lg-4 col-xl-3 my-7 d-flex">
                {renderProgram(
                  md.node.frontmatter.hero.img.childImageSharp.gatsbyImageData,
                  node.uri,
                  md.node.frontmatter.desc,
                  node.metaProgramFullName,
                  node.metaProgramIsEnrolling,
                  node.metaProgramType,
                )}
              </div>
            )
          })}
        </div>
        <CarouselProvider
          className="d-md-none overflow-hidden"
          currentSlide={1}
          naturalSlideWidth={100}
          isIntrinsicHeight
          totalSlides={sortedPrograms.length}>
          <Slider
            classNameTray="program-slider"
            style={{
              width: sliderWidth,
              overflow: 'visible',
              transform: `translateX(calc(50vw - ${(slideWidth + gutterWidth) / 2}${sliderUnit}))`,
              // transform: `translateX(25vw)`
            }}>
            {sortedPrograms.map(({ node }, idx) => {
              const md = _find(
                data.markdownPrograms.edges,
                (o) => o.node.frontmatter.programCode === node.metaProgramCode,
              )
              return (
                <Slide key={node.uri} index={idx} className="program-slide my-7 px-4 d-flex" innerClassName="d-flex">
                  {renderProgram(
                    md.node.frontmatter.hero.img.childImageSharp.gatsbyImageData,
                    node.uri,
                    md.node.frontmatter.desc,
                    node.metaProgramFullName,
                    node.metaProgramIsEnrolling,
                    node.metaProgramType,
                  )}
                </Slide>
              )
            })}
          </Slider>
        </CarouselProvider>
      </>
    )
  }

  // These are the panels that contain each of the program listings
  // The panels are controlled by the campus and course type tabs
  const renderPanels = () => (
    <>
      {/* This section only runs if the showOnline prop is true. This prop is true by default */}
      {showOnline && (
        <div id="OnlineCampusPanel" role="tabpanel" aria-labelledby="OnlineCampusTab">
          <div id="OnlineProgramTypeDegreePanel" role="tabpanel" aria-labelledby="OnlineProgramTypeDegreeTab">
            {campus === '45' && onlineType === 'degree' && renderPrograms(data.programsOnlineDegree.edges)}
          </div>
          <div id="OnlineProgramTypeDiplomaPanel" role="tabpanel" aria-labelledby="OnlineProgramTypeDiplomaTab">
            {campus === '45' && onlineType === 'diploma' && renderPrograms(data.programsOnlineDiploma.edges)}
          </div>
        </div>
      )}
      {/* This section only runs if the showClearwater prop is true. This prop is true by default */}
      {showClearwater && (
        <div id="ClearwaterCampusPanel" role="tabpanel" aria-labelledby="ClearwaterCampusTab">
          <div id="ClearwaterProgramTypeDiplomaPanel" role="tabpanel" aria-labelledby="ClearwaterProgramTypeDiplomaTab">
            {campus === '47' && clearwaterType === 'diploma' && renderPrograms(data.programsClearwaterDiploma.edges)}
          </div>
          <div id="ClearwaterProgramTypeDegreePanel" role="tabpanel" aria-labelledby="ClearwaterProgramTypeDegreeTab">
            {campus === '47' && clearwaterType === 'degree' && renderPrograms(data.programsClearwaterDegree.edges)}
          </div>
        </div>
      )}
    </>
  )

  return (
    <div className={className}>
      {/* If either prop is false, the tabs will not display; if both are true (default) tabs will display to switch from Online to CLearwater and vice versa */}
      {showClearwater && showOnline && (
        <div className="program-toggle">
          <div
            className="container text-lg-center"
            role="tablist"
            aria-orientation="horizontal"
            aria-label="Campus Tabs">
            <button
              active={campus === '45' ? 'true' : 'false'}
              aria-controls="OnlineCampusPanel"
              aria-selected={campus === '45' ? 'true' : 'false'}
              className="h4 blue py-5 px-lg-8 px-4 mx-3"
              id="OnlineCampusTab"
              onClick={() => setCampus('45')}
              role="tab"
              type="button">
              Online Campus
            </button>
            <button
              active={campus === '47' ? 'true' : 'false'}
              aria-controls="ClearwaterCampusPanel"
              aria-selected={campus === '47' ? 'true' : 'false'}
              className="h4 blue py-5 px-lg-8 px-4 mx-3"
              id="ClearwaterCampusTab"
              onClick={() => setCampus('47')}
              role="tab"
              type="button">
              Clearwater Campus
            </button>
          </div>
        </div>
      )}

      <div className="programs py-10">
        <div className="container" role="tablist" aria-orientation="horizontal" aria-label="Program Type Tabs">
          {campus === '45' && (
            <>
              <button
                aria-controls="OnlineProgramTypeDegreePanel"
                aria-selected={onlineType === 'degree' ? 'true' : 'false'}
                disabled={onlineType === 'degree'}
                id="OnlineProgramTypeDegreeTab"
                onClick={() => setOnlineType('degree')}
                role="tab"
                type="button">
                Associate Degrees
              </button>
              <span> | </span>
              <button
                aria-controls="OnlineProgramTypeDiplomaPanel"
                aria-selected={onlineType === 'diploma' ? 'true' : 'false'}
                disabled={onlineType === 'diploma'}
                id="OnlineProgramTypeDiplomaTab"
                onClick={() => setOnlineType('diploma')}
                role="tab"
                type="button">
                Diplomas
              </button>
              <span> | </span>
              <button
                aria-controls="OnlineProgramTypeEmployerPartnerPanel"
                aria-selected={onlineType === 'employerPartner' ? 'true' : 'false'}
                disabled={onlineType === 'employerPartner'}
                id="OnlineProgramTypeEmployerPartnerTab"
                onClick={() => setOnlineType('employerPartner')}
                role="tab"
                type="button">
                Employer Partner Programs
              </button>
            </>
          )}
          {campus === '47' && (
            <>
              <button
                aria-controls="ClearwaterProgramTypeDegreePanel"
                aria-selected={clearwaterType === 'degree' ? 'true' : 'false'}
                disabled={clearwaterType === 'degree'}
                id="ClearwaterProgramTypeDegreeTab"
                onClick={() => setClearwaterType('degree')}
                role="tab"
                type="button">
                Associate Degrees
              </button>
              <span> | </span>
              <button
                aria-controls="ClearwaterProgramTypeDiplomaPanel"
                aria-selected={clearwaterType === 'diploma' ? 'true' : 'false'}
                disabled={clearwaterType === 'diploma'}
                id="ClearwaterProgramTypeDiplomaTab"
                onClick={() => setClearwaterType('diploma')}
                role="tab"
                type="button">
                Diplomas
              </button>
            </>
          )}
        </div>
        {onlineType !== 'employerPartner' && <div className="container program-list">{renderPanels()}</div>}
        {onlineType === 'employerPartner' && (
          <div className="container">
            <Link to="/future-students/emt" className="d-inline-block my-7 emt-wrapper">
              <Card cardShadow="none" className="emt-card">
                <div className="">
                  <img src={EMTImage} alt="Emergency Medical Technician Diploma Program" className="mb-4" />
                  <p className="mb-1">
                    <strong>Emergency Medical Technician</strong>
                  </p>
                  <p className="card__sub">Diploma Program</p>
                  <p>Program available to Crisis Center of Tampa Bay&nbsp;employees.</p>
                </div>
                <div className="card-footer">
                  <div className="btn btn-outline-black mb-4">Learn More &gt;</div>
                </div>
              </Card>
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

export default styled(ProgramSelector)`
  .program-toggle {
    button {
      background-color: transparent;
      border: none;
      border-top-right-radius: 2px;
      border-top-left-radius: 2px;
      cursor: pointer;
      display: inline-block;
      margin: 0;
      transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out;

      &[active='true'] {
        background-color: ${colors.dev.gray.h()};
        color: ${colors.dev.gray.b()};
      }
    }
  }

  .programs {
    background-color: ${colors.dev.gray.h()};

    button {
      background: transparent;
      border: none;
      text-align: left;

      color: ${colors.dev.blue.d()};

      &[disabled] {
        color: ${colors.dev.gray.b()};
      }
    }

    ul {
      list-style: none;
      margin: 0;
      padding: 0;
    }

    li {
      display: inline-block;
    }
  }

  .program-slider {
    width: 100% !important;
  }

  .container.program-list {
    ${below.md`
      max-width: 100%;
      padding: 0;
  `}
  }

  .card__sub {
    color: ${colors.dev.gray.d()};
  }

  .program-card-wrapper {
    border-radius: 2px;
    display: block;
    max-width: 100%;
    transition: all 0.2s ease-in-out;

    &:hover {
      text-decoration: none;

      .btn {
        color: #fff;
        background-color: ${colors.dev.gray.b()};
      }

      .program-card {
        border: 1px solid ${colors.dev.gray.g()};
      }
    }

    .program-card {
      background: #fff;
      border: 1px solid transparent;
      max-width: 100%;
      width: 100%;
    }
  }
  .emt-wrapper {
    border: 1px solid transparent;
    &:hover {
      border: 1px solid ${colors.dev.gray.g()};
    }
    .emt-card {
      background: #fff;
      width: 20rem;
      text-decoration: none;
    }
      &:hover {
        text-decoration: none;
        .btn {
          color: #fff;
          background-color: ${colors.dev.gray.b()};
        }
      }
    }
  }
`

ProgramSelector.propTypes = {
  className: PropTypes.string,
  initialCampus: PropTypes.oneOf(['45', '47']),
  showClearwater: PropTypes.bool,
  showOnline: PropTypes.bool,
}

ProgramSelector.defaultProps = {
  className: '',
  initialCampus: '45',
  showClearwater: true,
  showOnline: true,
}
